import {
  Children,
  cloneElement,
  FC,
  isValidElement,
  MouseEvent,
  ReactHTMLElement,
  ReactNode,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';

import {
  generateUniqueID,
  useEscapeKey,
  useOnClickOutside,
} from 'shared/lib/utils';

import styles from './styles.module.scss';

export type TooltipProps = {
  children: ReactNode;
  title: ReactNode;
  childClassNames?: string;
  tooltipClassNames?: string;
  wrapperClassNames?: string;
  closeIconClassNames?: string;
  showCloseIcon?: boolean;
  showOnTouch?: boolean;
  showOnHover?: boolean;
  tooltipStateCallback?: (isTooltipVisible: boolean) => void;
  testId?: string;
  shouldAddAriaLabelledBy?: boolean;
  suppress?: boolean;
};

export const Tooltip: FC<TooltipProps> = ({
  children,
  title,
  childClassNames,
  tooltipClassNames,
  wrapperClassNames,
  closeIconClassNames,
  tooltipStateCallback,
  showCloseIcon = false,
  showOnTouch = true,
  showOnHover = true,
  testId = 'tooltip',
  shouldAddAriaLabelledBy = true,
  suppress = false,
}) => {
  const tooltipId = generateUniqueID();

  const tooltipRef = useRef<HTMLParagraphElement>(null);

  const [isVisible, setIsVisible] = useState(false);

  const showTooltip = () => {
    setIsVisible(true);
    tooltipStateCallback?.(true);
  };
  const hideTooltip = () => {
    setIsVisible(false);
    tooltipStateCallback?.(false);
  };

  const handleTooltipShow = () => {
    if (!showOnHover || suppress) return;

    showTooltip();
  };

  const handleTooltipHide = () => {
    if (!showOnHover || suppress) return;

    hideTooltip();
  };

  const handleClickOutside = () => {
    if (showOnHover) return;

    hideTooltip();
  };

  useOnClickOutside(tooltipRef, handleClickOutside);
  useEscapeKey(handleClickOutside);

  const getChildElement = () => {
    const child = Children.toArray(children)[0];

    if (!isValidElement(child)) return;

    const handleChildClick = (event: MouseEvent<HTMLElement>) => {
      if (suppress) return;

      const isTouchScreen =
        'pointerType' in event.nativeEvent &&
        event.nativeEvent.pointerType !== 'mouse';

      const shouldShowOnClick = !showOnHover || (isTouchScreen && showOnTouch);

      if (shouldShowOnClick) {
        showTooltip();
      } else {
        hideTooltip();
      }
      // event should be passed for correct walkthrough process
      child.props.onClick?.(event);
    };

    const childClone = cloneElement(child as ReactHTMLElement<HTMLElement>, {
      onMouseEnter: handleTooltipShow,
      onMouseLeave: handleTooltipHide,
      onFocus: handleTooltipShow,
      onBlur: handleTooltipHide,
      onClick: handleChildClick,
      ...(shouldAddAriaLabelledBy ? { 'aria-labelledby': tooltipId } : {}),
    });

    if (childClassNames)
      return <div className={childClassNames}>{childClone}</div>;

    return childClone;
  };

  return (
    <div
      className={clsx(styles.wrapper, wrapperClassNames)}
      aria-describedby={tooltipId}
    >
      {getChildElement()}
      <p
        id={tooltipId}
        ref={tooltipRef}
        className={clsx(styles.tooltip, tooltipClassNames, {
          [styles.visible]: isVisible,
          [styles.interactable]: isVisible && !showOnHover,
        })}
        role="tooltip"
        data-testid={testId}
      >
        {title}
        {showCloseIcon && (
          <button
            type="button"
            className={clsx(
              'btn btn-icon-ghost icon-v2-close',
              closeIconClassNames,
              styles.closeIcon,
            )}
            onClick={hideTooltip}
            onBlur={hideTooltip}
            aria-label="Close tooltip popup"
            disabled={!isVisible}
          />
        )}
      </p>
    </div>
  );
};
